import { create } from "zustand";

/**
 * Theme + mode state.
 *
 * Both values are mirrored onto the <html> element as data-theme / data-mode
 * attributes; that's what the CSS variable themes in web/shared/css key off.
 * State is persisted to localStorage so reloads stay sticky, and the inline
 * <script> in index.html applies the persisted values before paint to avoid
 * a flash of the wrong theme.
 */

export type Theme = "dataflowx" | "ember";
export type Mode = "light" | "dark";

const THEME_KEY = "ir.theme";
const MODE_KEY = "ir.mode";

const isBrowser = typeof window !== "undefined";

function readTheme(): Theme {
    if (!isBrowser) return "dataflowx";
    const stored = window.localStorage.getItem(THEME_KEY);
    return stored === "ember" ? "ember" : "dataflowx";
}

function readMode(): Mode {
    if (!isBrowser) return "dark";
    const stored = window.localStorage.getItem(MODE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function applyTheme(theme: Theme): void {
    if (!isBrowser) return;
    document.documentElement.setAttribute("data-theme", theme);
}

function applyMode(mode: Mode): void {
    if (!isBrowser) return;
    document.documentElement.setAttribute("data-mode", mode);
}

interface ThemeStore {
    theme: Theme;
    mode: Mode;
    setTheme: (theme: Theme) => void;
    setMode: (mode: Mode) => void;
    toggleMode: () => void;
}

export const useTheme = create<ThemeStore>((set, get) => ({
    theme: readTheme(),
    mode: readMode(),
    setTheme: (theme) => {
        applyTheme(theme);
        if (isBrowser) window.localStorage.setItem(THEME_KEY, theme);
        set({ theme });
    },
    setMode: (mode) => {
        applyMode(mode);
        if (isBrowser) window.localStorage.setItem(MODE_KEY, mode);
        set({ mode });
    },
    toggleMode: () => {
        const next: Mode = get().mode === "dark" ? "light" : "dark";
        get().setMode(next);
    },
}));

/** Apply the current store values to the DOM. Called once at app boot. */
export function bootstrapTheme(): void {
    const { theme, mode } = useTheme.getState();
    applyTheme(theme);
    applyMode(mode);
}
